import React, { FunctionComponent } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { User } from '../../../../models/User';
import { MollieLink } from '../../../../models/MollieLink';

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// const { DateTime } = require("luxon");

type Props = {
  object: User;
  setPremiumPaymentsOpen: (b:boolean) => void;
}

export const CoachUserWidgetsMollieLinksSmall: FunctionComponent<Props> = ({
  object,
  setPremiumPaymentsOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  let sentCount = 0;
  let paidCount = 0;

  let is_payments = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'payments'});

  // Coach-level widget settings (the `show_links` list toggle and the `cta`
  // goto-button toggle, both on by default). Stored on
  // login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.mollieLinksSmallValueKey];
  const showCta = HelpersClientWidgets.getMollieLinksSmallCtaEnabled(cfg);
  const showLinks = HelpersClientWidgets.getMollieLinksSmallLinksEnabled(cfg);

  // Last-5 payment links (newest first) + the total sent count. `d.count` is
  // the total matching the filter regardless of per_page, so this one query
  // feeds both the rows and the "y sent" half of the summary line.
  const {
    data: mollieLinks,
  } = useQuery({
    queryKey: ["mollie_links", object?.id],
    queryFn: () =>
      fetch_all_with_count<MollieLink>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'mollie_link',
          fields: ['id', 'created_at', 'description', 'amount', 'paid'],
          order: 'id DESC',
          per_page: 5,
          page: 0,
          filter: {
            search: '',
            advanced: {
              for_client: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        sentCount = d.count;
        return d.result;
      },
    enabled: !!object?.id && !!(login?.practice_id || currentPractice?.id),
  });

  // Paid-only count for the "x paid" half of the summary line. Same shape as
  // the sent-count query (per_page 1 — only the count is read), mirroring the
  // questionnaires widget's completed-count query.
  useQuery({
    queryKey: ["mollie_links_paid", object?.id],
    queryFn: () =>
      fetch_all_with_count<MollieLink>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'mollie_link',
          fields: ['id'],
          per_page: 1,
          page: 0,
          filter: {
            search: '',
            advanced: {
              for_client: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id),
              paid: '1'
            }
          }
        },
        login
      ),
      select: (d) => {
        paidCount = d.count;
        return d.result;
      },
    enabled: !!object?.id && !!(login?.practice_id || currentPractice?.id),
  });

  const lastLinks = (mollieLinks || []).slice(0, 5);

  // Amounts render as proper money (grouped thousands, 2 decimals) instead
  // of the raw number, e.g. 1200.5 -> "1.200,50". Locale follows the resolved
  // i18n language (nl default), same fallback the API calls use.
  const formatAmount = (amount?: number) => new Intl.NumberFormat(
    i18n.resolvedLanguage || 'nl',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  ).format(amount || 0);

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row
        (space-saving — the old fullwidth bottom button cost a full row).
        Gated by the coach's `cta` setting; the premium lock renders as the
        star overlay inline with the label. */}
    {showCta && <Button
      label={t("coach.general.actions.to_mollie_links", "Naar de betaallinks")}
      id="goto_mollie_links"
      text
      size="small"
      premiumLocked={!is_payments}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        if (is_payments) {
          navigate(`/user/${object.id}/payments`);
        } else {
          setPremiumPaymentsOpen(true);
        }
      }}
    />}

    <Grid container spacing={1} sx={{position: 'relative'}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.mollie_links")}</Typography>
        <Divider />
      </Grid>

      {/* Summary counts: one i18n plural pair per metric (paid / sent),
          separated by a middot. Only meaningful when at least one link was
          sent. */}
      {sentCount > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t("coach.submenu.detail.mollie_links_paid_count", {count: paidCount})} · ${t("coach.submenu.detail.mollie_links_sent_count", {count: sentCount})}`}
        </Typography>
      </Grid>}

      {/* Last 5 links: description + amount + paid status, a Divider between
          consecutive rows. Toggled by the show_links setting (the summary
          counts line stays regardless). */}
      {showLinks && lastLinks.map((link, idx) => <React.Fragment key={link.id}>
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{link.description || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{color: 'text.secondary'}}>{formatAmount(link.amount)} EUR</Typography>
            </Grid>
          </Grid>
          {!!link.paid && <Check color='primary' sx={{marginBottom: -0.75}} />}
          {!link.paid && <Close color='secondary' sx={{marginBottom: -0.75}} />}
        </Grid>
        {idx < lastLinks.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}
    </Grid>
  </Paper>;
}
